import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { getFieldErrors } from "../helpers";
import { tryConfirmLogin } from "./authReducer";


export interface INotification {
    _id: string;
    msg: string;
    actorUserId: string;
    action: string;
    resourceType: string;
    resourceId: string;
    orderId: string;
    indicates: string;
    createdAt: string;
}


export interface INotificationsState {
    fetchingNotifications: boolean;
    notifications: INotification[];
    fetchNotificationsErr: string | null;
    deletingNotification: boolean;
    deletedNotification: boolean;
    deleteNotificationErr: string | null;
}

const initialState = {
    fetchingNotifications: false,
    notifications: [],
    fetchNotificationsErr: null,
    deletingNotification: false,
    deletedNotification: false,
    deleteNotificationErr: null
} as INotificationsState;

const notificationsSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        clearFetchNotificationsErr: (state: INotificationsState) => {
            state.fetchNotificationsErr = null;
        },
        fetchNotificationsStart: (state: INotificationsState) => {
            state.fetchingNotifications = false;
            state.fetchNotificationsErr = null;
        },
        fetchNotificationsPass: (state: INotificationsState, action: PayloadAction<INotification[]>) => {
            state.fetchingNotifications = false;
            state.notifications = action.payload;
        },
        fetchNotificationsFail: (state: INotificationsState, action: PayloadAction<string>) => {
            state.fetchingNotifications = false;
            state.fetchNotificationsErr = action.payload;
        },
        clearDeleteNotificationErr: (state: INotificationsState) => {
            state.deleteNotificationErr = null
        },
        clearDeletedNotification: (state: INotificationsState) => {
            state.deletedNotification = false;
        },
        deleteNotificationStart: (state: INotificationsState) => {
            state.deletingNotification = true;
            state.deletedNotification = false;
            state.deleteNotificationErr = null;
        },
        deleteNotificationPass: (state: INotificationsState) => {
            state.deletingNotification = false;
            state.deletedNotification = true;
        },
        deleteNotificationFail: (state: INotificationsState, action: PayloadAction<string>) => {
            state.deletingNotification = false;
            state.deleteNotificationErr = action.payload;
        }
    }
});

export const {
    clearFetchNotificationsErr,
    fetchNotificationsStart,
    fetchNotificationsPass,
    fetchNotificationsFail,
    clearDeleteNotificationErr,
    clearDeletedNotification,
    deleteNotificationStart,
    deleteNotificationPass,
    deleteNotificationFail
} = notificationsSlice.actions;


interface ITryFetchNotificationsArg {
    authToken: string;
}

export const tryFetchNotifications = createAsyncThunk('notifications/tryFetchNotifications', (arg: ITryFetchNotificationsArg, { dispatch }) => {
    const { authToken } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/notifications`

    dispatch(fetchNotificationsStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(fetchNotificationsPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({authToken}));
            return;
        }

        if (data?.err) {
            dispatch(fetchNotificationsFail(data.err));
        } else {
            dispatch(fetchNotificationsFail('Check your network connection.'));
        }
    })
});

interface ITryDeleteNotificationArg {
    authToken: string;
    id: string;
}

export const tryDeleteNotification = createAsyncThunk('notifications/tryDeleteNotification', (arg: ITryDeleteNotificationArg, { dispatch }) => {
    const { authToken, id } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/notifications/${id}`;

    dispatch(deleteNotificationStart());
    axios.delete(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(deleteNotificationPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({authToken}));
            return;
        }
        if (data?.errors) {
            dispatch(deleteNotificationFail(getFieldErrors(data.errors)[0].msg));
        } else if (data?.err) {
            dispatch(deleteNotificationFail(data.err));
        } else {
            dispatch(deleteNotificationFail('Check your network connection.'));
        }
    })
})

export default notificationsSlice.reducer;